import { Actions } from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { useEffect, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { createUseVM } from './form.utils';
import { toHIITExercise, toSet, toTabataExercise } from './workout.mappers';
import { hiitExerciseSchema, tabataExerciseSchema } from './workout.schema';
import { WorkoutVM } from './workout.viewModel';
import { sprinkles } from '@design-system/sprinkles.css';
import { HIITExercise, TabataExercise } from './workouts.domain';
import { Rep } from '../model/workout.domain';
import { LibraryHintType } from '../view/components/common/LibraryHint';

type SubExerciseVMActionMap = {
  AddSet: null;
  RemoveSet: { setIndex: number };
  SetAMRAP: boolean;
  AddMedia: null;
  Remove: null;
};

type SubExerciseVMAction = Actions<SubExerciseVMActionMap>;

export type SubExerciseVM = ReturnType<typeof useSubExerciseVM>;

export const useSubExerciseVM = (
  workoutVM: WorkoutVM,
  subExerciseId: string,
) => {
  const [isAMRAP, setIsAMRAP] = useState(false);
  const [libraryHint, setLibraryHint] = useState<LibraryHintType | null>(null);

  const { type } = workoutVM.exerciseMap[subExerciseId];

  const vm = createUseVM(
    type === 'HIITExercise' ? hiitExerciseSchema : tabataExerciseSchema,
    (v) =>
      type === 'HIITExercise'
        ? toHIITExercise(v as HIITExercise['value'])
        : toTabataExercise(v as TabataExercise['value']),
  )(workoutVM, subExerciseId);

  const { append, remove, fields } = useFieldArray({
    control: vm.control,
    name: 'sets',
  });

  const sets = vm.watch('sets');
  const fieldsWithSets = fields.map((field, index) => ({
    ...field,
    ...sets[index],
  }));

  useEffect(() => {
    const amrapDisabled = Object.values(fieldsWithSets).some(
      (set) => set.rep.type !== 'AMRAP',
    );
    setIsAMRAP(!amrapDisabled);
  }, [fieldsWithSets]);

  const handleAMRAPChange = (value: boolean) => {
    setIsAMRAP(value);
    if (!value) return;

    vm.sets.forEach((_, i) => {
      vm.setValue(`sets.${i}.rep.type`, 'AMRAP');
      vm.setValue(`sets.${i}.rep.value`, 0);
    });
  };

  const settings = workoutVM.getSettings(subExerciseId);
  const exerciseSuggestions =
    workoutVM.exerciseSuggestions.type === 'fetched'
      ? workoutVM.exerciseSuggestions.value
      : [];
  const titleField = vm.register('name', { noValue: true });

  const setTitleValue = (value: string) => {
    vm.setValue('name', value, { shouldValidate: true });
    vm.tick();
  };

  const setTitleSearch = (query: string) => {
    workoutVM.dispatch('setExerciseSearch', { subExerciseId, query });
  };

  const clearTitleSearch = () => workoutVM.dispatch('clearExerciseSearch');

  const applySuggestion = (description: string, uuid: string) => {
    const currentDescription = vm.watch('description');

    if (!currentDescription) {
      vm.setValue('description', description, { shouldValidate: true });
      vm.tick();
      clearTitleSearch();
      return;
    }

    const suggestionTitle = exerciseSuggestions.find(
      (s) => s.id === uuid,
    )?.name;
    setLibraryHint({ title: suggestionTitle, description });
    clearTitleSearch();
  };

  const applyLibraryHint = (hint: LibraryHintType) => {
    if (hint.title) {
      setTitleValue(hint.title);
    }

    if (hint.description) {
      vm.setValue('description', hint.description, { shouldValidate: true });
      vm.tick();
    }

    setLibraryHint(null);
    clearTitleSearch();
  };

  const dispatch = (a: SubExerciseVMAction) => {
    switch (a.type) {
      case 'AddSet':
        append(
          toSet({
            time: settings.time,
            rest: settings.rest,
            rep: fieldsWithSets[0].rep as Rep,
          }),
        );
        vm.tick();
        break;

      case 'RemoveSet':
        remove(a.payload.setIndex);
        vm.tick();
        break;

      case 'SetAMRAP':
        handleAMRAPChange(a.payload);
        break;

      case 'AddMedia':
        workoutVM.dispatch('openLibrary', {
          action: 'update',
          parentId: settings.id,
          exerciseId: subExerciseId,
        });
        break;

      case 'Remove':
        workoutVM.dispatch('removeSubExercise', { exerciseId: subExerciseId });
        break;
    }
  };

  const dispatchers = getDispatchers<SubExerciseVMActionMap>(dispatch);

  const menuItems = [
    {
      label: 'Remove',
      onClick: dispatchers.lazyDispatch('Remove'),
      className: sprinkles({ color: 'ERROR-100' }),
    },
  ];

  return {
    isAMRAP,
    menuItems,
    exerciseSuggestions,
    setTitleValue,
    setTitleSearch,
    clearTitleSearch,
    applySuggestion,
    libraryHint,
    applyLibraryHint,
    titleError: titleField.error,
    ...vm,
    ...dispatchers,
    fieldsWithSets,
  };
};
